'use client';

import {getAppInsights} from './appInsights';

/**
 * Application Insights severity levels
 * @see https://learn.microsoft.com/en-us/azure/azure-monitor/app/data-model-trace-telemetry
 */
enum SeverityLevel {
  Verbose = 0,
  Information = 1,
  Warning = 2,
  Error = 3,
  Critical = 4,
}

type LogProperties = Record<string, unknown>;

interface Logger {
  debug(message: string, properties?: LogProperties): void;
  info(message: string, properties?: LogProperties): void;
  warn(message: string, properties?: LogProperties): void;
  error(message: string, properties?: LogProperties): void;
}

function flattenErrorChain(error: Error, prefix: string, result: LogProperties): void {
  result[`${prefix}Name`] = error.name;
  result[`${prefix}Message`] = error.message;
  result[`${prefix}Stack`] = error.stack;

  if (error.cause instanceof Error) {
    flattenErrorChain(error.cause, `${prefix}Cause`, result);
  } else if (error.cause !== undefined) {
    // Handle non-Error cause values
    result[`${prefix}Cause`] = String(error.cause);
  }
}

function flattenErrorProperties(properties?: LogProperties): LogProperties | undefined {
  if (!properties) return properties;

  const result = {...properties};

  if (result.error instanceof Error) {
    flattenErrorChain(result.error, 'error', result);
    delete result.error;
  }

  return result;
}

function createLogger(): Logger {
  const log = (
    level: SeverityLevel,
    message: string,
    properties?: LogProperties
  ): void => {
    const appInsights = getAppInsights();
    const timestamp = new Date().toISOString();
    const flattenedProperties = flattenErrorProperties(properties);

    // Always log to console in development
    if (process.env.NODE_ENV === 'development') {
      const consoleMethod = getConsoleMethod(level);
      consoleMethod(`[${timestamp}] ${message}`, flattenedProperties ?? '');
    }

    // Send to Application Insights if available
    if (appInsights) {
      // Track exception if error property is an Error instance
      if (properties?.error instanceof Error) {
        appInsights.trackException({
          exception: properties.error,
          severityLevel: level,
          properties: flattenedProperties as Record<string, string>,
        });
      }

      appInsights.trackTrace({
        message,
        severityLevel: level,
        properties: flattenedProperties as Record<string, string>,
      });
    }
  };

  return {
    debug: (message, properties) => log(SeverityLevel.Verbose, message, properties),
    info: (message, properties) => log(SeverityLevel.Information, message, properties),
    warn: (message, properties) => log(SeverityLevel.Warning, message, properties),
    error: (message, properties) => log(SeverityLevel.Error, message, properties),
  };
}

function getConsoleMethod(level: SeverityLevel): typeof console.log {
  switch (level) {
    case SeverityLevel.Verbose:
      return console.debug;
    case SeverityLevel.Information:
      return console.info;
    case SeverityLevel.Warning:
      return console.warn;
    case SeverityLevel.Error:
    case SeverityLevel.Critical:
      return console.error;
    default:
      return console.log;
  }
}

export const logger = createLogger();

export default logger;
